import { linter, Diagnostic } from '@codemirror/lint';
import type { Text } from '@codemirror/state';
import Ajv from 'ajv';
import { load } from 'js-yaml';
import { useMemo, useRef, useState } from 'react';

// js-yaml pads its input with a trailing newline, so an error at the end of
// the input (an unclosed bracket or quote while typing) is reported on the line
// after the last one, which `doc.line()` would throw on.
const positionOf = (doc: Text, line: number, column = 0) => {
  if (line > doc.lines) {
    return doc.length;
  }
  const { from, to } = doc.line(Math.max(line, 1));
  return Math.min(from + column, to);
};

export const useYamlLinter = (schema: object) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  const ajvValidate = useMemo(() => {
    const ajv = new Ajv({ allErrors: true });
    return ajv.compile(schema);
  }, [schema]);

  const lastErrorsKeyRef = useRef<string>('');
  const lastWarningsKeyRef = useRef<string>('');

  const updateCounts = (nextErr: string[], nextWarn: string[]) => {
    const errKey = nextErr.join('||');
    const warnKey = nextWarn.join('||');
    if (errKey !== lastErrorsKeyRef.current) {
      setErrors(nextErr);
      lastErrorsKeyRef.current = errKey;
    }
    if (warnKey !== lastWarningsKeyRef.current) {
      setWarnings(nextWarn);
      lastWarningsKeyRef.current = warnKey;
    }
  };

  const lastDiagnosticsKeyRef = useRef<string>('');
  const lastDiagnosticsRef = useRef<readonly Diagnostic[] | null>(null);

  const yamlLinter = useMemo(
    () =>
      linter(
        view => {
          const diagnostics: Diagnostic[] = [];
          const text = view.state.doc.toString();
          const nextErrors: string[] = [];
          const nextWarnings: string[] = [];

          let parsed: any = undefined;
          try {
            parsed = load(text);
          } catch (e: any) {
            const mark = e?.mark;
            if (mark && typeof mark.line === 'number') {
              const { doc } = view.state;
              const from = positionOf(doc, mark.line + 1, mark.column || 0);
              const to = Math.min(from + 1, doc.length);
              diagnostics.push({
                from,
                to,
                severity: 'error',
                message: `YAML: ${e.message.replace(/ *at line.*$/, '')}`,
              });
              const line = doc.lineAt(from);
              nextErrors.push(
                `YAML parse error on line ${line.number}, column ${
                  from - line.from + 1
                }`,
              );
            } else {
              const lineMatch = /line (\d+)/i.exec(e.message || '');
              if (lineMatch) {
                const { doc } = view.state;
                const line = doc.lineAt(
                  positionOf(doc, parseInt(lineMatch[1], 10)),
                );
                const lineNum = line.number;
                diagnostics.push({
                  from: line.from,
                  to: line.to,
                  severity: 'error',
                  message: `YAML: ${e.message}`,
                });
                nextErrors.push(`YAML parse error on line ${lineNum}`);
              } else {
                diagnostics.push({
                  from: 0,
                  to: Math.min(1, view.state.doc.length),
                  severity: 'error',
                  message: `YAML: ${e.message || 'Unknown parse error'}`,
                });
                nextErrors.push(`YAML parse error`);
              }
            }
            updateCounts(nextErrors, []);
            return diagnostics;
          }

          if (parsed === null || parsed === undefined) {
            updateCounts(nextErrors, nextWarnings);
            return diagnostics;
          }

          const valid = ajvValidate(parsed);
          if (!valid && ajvValidate.errors && ajvValidate.errors.length > 0) {
            // Group errors by instancePath, then by keyword
            type AjvErr = {
              instancePath?: string;
              keyword: string;
              message?: string;
              params?: any;
            };
            const ajvErrors = ajvValidate.errors as AjvErr[];

            const byPath = new Map<string, Map<string, AjvErr[]>>();
            for (const err of ajvErrors) {
              const path = err.instancePath || '/';
              const kw = err.keyword || 'other';
              const perKw = byPath.get(path) ?? new Map<string, AjvErr[]>();
              if (!byPath.has(path)) byPath.set(path, perKw);
              const arr = perKw.get(kw) ?? [];
              arr.push(err);
              if (!perKw.has(kw)) perKw.set(kw, arr);
            }

            const normalizePath = (p: string) => {
              const cleaned = p.replace(/^\//, '').replace(/\//g, '.');
              return cleaned || '';
            };

            const msgLines: string[] = [];
            for (const [path, kwMap] of byPath.entries()) {
              for (const [kw, arr] of kwMap.entries()) {
                if (kw === 'required') {
                  const props = Array.from(
                    new Set(
                      arr.map(e => e.params?.missingProperty).filter(Boolean),
                    ),
                  );
                  if (props.length > 0) {
                    msgLines.push(
                      `${normalizePath(
                        path,
                      )} must have required property: [${props
                        .map(p => `'${p}'`)
                        .join(', ')}].`,
                    );
                  }
                } else if (kw === 'additionalProperties') {
                  const extras = Array.from(
                    new Set(
                      arr
                        .map(e => e.params?.additionalProperty)
                        .filter(Boolean),
                    ),
                  );
                  if (extras.length > 0) {
                    msgLines.push(
                      `${normalizePath(
                        path,
                      )} must NOT have additional properties: [${extras
                        .map(p => `'${p}'`)
                        .join(', ')}].`,
                    );
                  }
                } else if (kw === 'oneOf') {
                  msgLines.push(
                    `${normalizePath(
                      path,
                    )} must match exactly one schema in oneOf.`,
                  );
                } else {
                  const uniqueMsgs = Array.from(
                    new Set(arr.map(e => e.message).filter(Boolean)),
                  );
                  if (uniqueMsgs.length > 0) {
                    msgLines.push(
                      `${normalizePath(path)} ${uniqueMsgs.join(', ')}`,
                    );
                  }
                }
              }
            }

            msgLines.forEach(msg => {
              const line = view.state.doc.line(1);
              diagnostics.push({
                from: line.from,
                to: line.to,
                severity: 'warning',
                message: `Schema: ${msg || 'Schema validation failed.'}`,
              });
              nextWarnings.push(msg);
            });
          }
          updateCounts(nextErrors, nextWarnings);

          const diagKey = diagnostics
            .map(d => `${d.severity}:${d.from}-${d.to}:${d.message}`)
            .join('||');
          if (
            diagKey === lastDiagnosticsKeyRef.current &&
            lastDiagnosticsRef.current
          ) {
            return lastDiagnosticsRef.current;
          }
          lastDiagnosticsKeyRef.current = diagKey;
          lastDiagnosticsRef.current = diagnostics;
          return diagnostics;
        },
        { delay: 400 },
      ),
    [ajvValidate],
  );

  return { yamlLinter, errors, warnings };
};
